import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Form, Input, Modal, Select, Space, message } from 'antd';
import { useState } from 'react';
import axios from '../config/axios';
import { Retailer } from '../types/retailers.types';
import { handleAPIError } from '../utils/helpers';

const { Option } = Select;

type AccountFormValues = {
	retailer: number;
	type: 'mobile_money' | 'bank';
	account_number: string;
	bank_name?: string;
	branch_name?: string;
};

interface CreateRetailerPaymentAccountProps {
	open: boolean;
	onClose: () => void;
	preSelectedRetailerId?: number;
}

const CreateRetailerPaymentAccount = ({ open, onClose, preSelectedRetailerId }: CreateRetailerPaymentAccountProps) => {
	const [accountForm] = Form.useForm<AccountFormValues>();
	const [accountType, setAccountType] = useState<'mobile_money' | 'bank'>('mobile_money');
	const queryClient = useQueryClient();

	// Fetch retailers for dropdown (only if no preselected retailer)
	const { data: retailersData } = useQuery({
		queryKey: ['retailers-dropdown'],
		queryFn: async () => {
			const response = await axios.get('/admin/retailers', {
				withCredentials: true,
				params: { page: 1, limit: 1000 }, // Get all retailers for dropdown
			});
			return response.data;
		},
		enabled: open && !preSelectedRetailerId,
	});

	// Add payment account mutation
	const { mutate: addAccount, isPending: isAddingAccount } = useMutation({
		mutationFn: async (values: AccountFormValues) => {
			const response = await axios.post('/admin/retailers/payment-accounts', values, {
				withCredentials: true,
			});
			return response.data;
		},
		onSuccess: () => {
			message.success('Payment account created successfully');
			queryClient.invalidateQueries({ queryKey: ['retailer-payment-accounts'] });
			queryClient.invalidateQueries({ queryKey: ['retailers'] });
			onClose();
			accountForm.resetFields();
		},
		onError: (error) => {
			handleAPIError(error);
		},
	});

	const handleAddAccount = (values: AccountFormValues) => {
		addAccount(values);
	};

	const handleModalClose = () => {
		onClose();
		accountForm.resetFields();
		setAccountType('mobile_money');
	};

	const retailers: Retailer[] = retailersData?.results || [];

	return (
		<Modal
			title="Create Payment Account for Retailer"
			open={open}
			onCancel={handleModalClose}
			footer={null}
			destroyOnClose
			width={600}
		>
			<Form
				form={accountForm}
				layout="vertical"
				onFinish={handleAddAccount}
				initialValues={{
					type: 'mobile_money',
					retailer: preSelectedRetailerId,
				}}
				requiredMark={false}
			>
				<Form.Item
					name="retailer"
					label="Retailer"
					rules={[{ required: true, message: 'Please select a retailer' }]}
				>
					<Select
						placeholder="Select retailer"
						showSearch
						optionFilterProp="children"
						disabled={!!preSelectedRetailerId}
						filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}
					>
						{retailers.map((retailer) => (
							<Option
								key={retailer.id}
								value={retailer.id}
							>
								{retailer.business_name} - {retailer.vendor_id}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="type"
					label="Account Type"
					rules={[{ required: true, message: 'Please select account type' }]}
				>
					<Select onChange={(value) => setAccountType(value as 'mobile_money' | 'bank')}>
						<Option value="mobile_money">Mobile Money</Option>
						<Option value="bank">Bank Account</Option>
					</Select>
				</Form.Item>

				<Form.Item
					name="account_number"
					label="Account Number"
					rules={[{ required: true, message: 'Please enter account number' }]}
				>
					<Input placeholder="Enter account number" />
				</Form.Item>

				{accountType === 'bank' && (
					<>
						<Form.Item
							name="bank_name"
							label="Bank Name"
							rules={[{ required: true, message: 'Please enter bank name' }]}
						>
							<Input placeholder="Enter bank name" />
						</Form.Item>

						<Form.Item
							name="branch_name"
							label="Branch Name"
							rules={[{ required: true, message: 'Please enter branch name' }]}
						>
							<Input placeholder="Enter branch name" />
						</Form.Item>
					</>
				)}

				<Divider />

				<div className="flex justify-end">
					<Space>
						<Button onClick={handleModalClose}>Cancel</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={isAddingAccount}
						>
							Create Account
						</Button>
					</Space>
				</div>
			</Form>
		</Modal>
	);
};

export default CreateRetailerPaymentAccount;
